/**
 * Reverse-Tag Inferencer (Patent Sub-system L)
 * =============================================
 * For every untagged cloud resource, infer the cost-center owner from
 * CloudTrail creator + name patterns + neighbour tags + Terraform module
 * path. One-click confirm applies the tag.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Tag, Search, Sparkles, Loader2, FlaskConical, CheckCircle2,
  AlertTriangle, X, FileSearch, Brain,
} from 'lucide-react';
import { toast } from 'sonner';
import DemoDataBanner from '../components/DemoDataBanner';
import BrandTitle from '../components/BrandTitle';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtUsd = (n) => {
  if (n == null) return '$0';
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
  return `$${n.toFixed(2)}`;
};

const ReverseTag = () => {
  const [signals, setSignals] = useState([]);
  const [resources, setResources] = useState([]);
  const [selected, setSelected] = useState(null);
  const [inference, setInference] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inferring, setInferring] = useState(false);
  const [confirmedIds, setConfirmedIds] = useState(new Set());

  useEffect(() => {
    Promise.all([
      axios.get(`${API_URL}/reverse-tag/signals`),
      axios.get(`${API_URL}/reverse-tag/untagged-public`),
    ]).then(([sigRes, resRes]) => {
      setSignals(sigRes.data.signals || []);
      setResources(resRes.data.resources || []);
    }).finally(() => setLoading(false));
  }, []);

  const runInfer = async (resource) => {
    setSelected(resource);
    setInferring(true);
    setInference(null);
    try {
      const r = await axios.post(`${API_URL}/reverse-tag/infer-public`, { resource_id: resource.resource_id });
      setInference(r.data);
    } catch (e) {
      toast.error('Inference failed');
    } finally {
      setInferring(false);
    }
  };

  const confirmInference = () => {
    if (!inference || !inference.inferred_cost_center) return;
    setConfirmedIds((prev) => new Set(prev).add(inference.resource_id));
    toast.success(`Tagged ${inference.resource_id.slice(0, 22)} → ${inference.inferred_cost_center}`);
    // In production this would call /api/reverse-tag/confirm
  };

  const totalUntaggedSpend = resources.reduce((sum, r) => sum + (r.monthly_cost_usd || 0), 0);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="reverse-tag-page">
      <Helmet>
        <title>Reverse-Tag Inferencer — AI Cost-Center Attribution | CARTIEAI</title>
        <meta name="description" content="The end of '82% untagged resources.' For every untagged cloud resource, we infer the owner from telemetry. One-click confirms apply the tag. Patent-pending sub-system L." />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <DemoDataBanner />
        {/* HERO */}
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-violet-300 border border-violet-500/30 rounded-md px-2 py-1 mb-5" data-testid="rt-patent-badge">
            <FlaskConical className="w-3 h-3" />
            Proprietary engine
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-[1.05]"><BrandTitle>Reverse-Tag Inferencer</BrandTitle></h1>
          <p className="mt-4 text-base sm:text-lg text-slate-400 leading-relaxed">
            The end of <span className="text-white font-semibold">&quot;82% untagged resources.&quot;</span> CloudHealth has tag policies. We use an <span className="text-violet-300">LLM classifier over telemetry</span> to infer the owner — then one-click apply.
          </p>
          <p className="mt-3 text-sm text-slate-500">
            CloudTrail creator IAM + resource name pattern + neighbour tags + Terraform module path → probabilistic cost-center inference with confidence score. Confirm with one click. Reject and feed back for tuning.
          </p>
        </div>

        {/* SUMMARY */}
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Card className="bg-slate-900/60 border-white/10">
            <CardContent className="p-4">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Untagged resources</div>
              <div className="text-3xl font-bold text-white mt-1" data-testid="rt-summary-count">{resources.length}</div>
              <div className="text-xs text-slate-500 mt-1">demo seed</div>
            </CardContent>
          </Card>
          <Card className="bg-slate-900/60 border-white/10">
            <CardContent className="p-4">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Monthly spend at risk</div>
              <div className="text-3xl font-bold text-amber-300 mt-1" data-testid="rt-summary-spend">{fmtUsd(totalUntaggedSpend)}</div>
              <div className="text-xs text-slate-500 mt-1">cannot be allocated today</div>
            </CardContent>
          </Card>
          <Card className="bg-slate-900/60 border-white/10">
            <CardContent className="p-4">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Resolved this session</div>
              <div className="text-3xl font-bold text-emerald-300 mt-1" data-testid="rt-summary-resolved">{confirmedIds.size}</div>
              <div className="text-xs text-slate-500 mt-1">confirmed inferences</div>
            </CardContent>
          </Card>
        </div>

        {/* MAIN UI */}
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* LEFT — resource list */}
          <Card className="lg:col-span-2 bg-slate-900/60 border-white/10">
            <CardHeader>
              <CardTitle className="text-white text-lg flex items-center gap-2">
                <FileSearch className="w-5 h-5 text-violet-300" /> Untagged resources
              </CardTitle>
              <CardDescription>Click any resource to run the inferencer.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2 max-h-[640px] overflow-auto">
              {loading && <Loader2 className="w-6 h-6 animate-spin mx-auto my-8 text-slate-500" />}
              {resources.map((r) => {
                const isSel = selected?.resource_id === r.resource_id;
                const isDone = confirmedIds.has(r.resource_id);
                return (
                  <button
                    key={r.resource_id}
                    onClick={() => runInfer(r)}
                    className={`w-full text-left px-3 py-2.5 rounded border transition ${isSel ? 'border-violet-500/50 bg-violet-500/10' : 'border-white/10 hover:bg-slate-950/60'} ${isDone ? 'opacity-50' : ''}`}
                    data-testid={`rt-resource-${r.resource_id.slice(0, 8)}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <Badge variant="outline" className="text-[10px] border-white/10 text-slate-400 font-mono">{r.resource_type}</Badge>
                      <span className="text-xs text-amber-300 font-mono font-semibold">{fmtUsd(r.monthly_cost_usd)}/mo</span>
                    </div>
                    <div className="text-sm text-white font-mono truncate">{r.name}</div>
                    <div className="text-[10px] text-slate-500 mt-1">{r.region} · created by <span className="text-slate-300">{r.creator_iam}</span></div>
                    {isDone && (
                      <div className="text-[10px] text-emerald-400 mt-1 flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3" /> Tagged this session
                      </div>
                    )}
                  </button>
                );
              })}
            </CardContent>
          </Card>

          {/* RIGHT — inference panel */}
          <Card className="lg:col-span-3 bg-slate-900/60 border-white/10">
            <CardHeader>
              <CardTitle className="text-white text-lg flex items-center gap-2">
                <Brain className="w-5 h-5 text-violet-300" /> Inferred owner
              </CardTitle>
              <CardDescription>Signals + confidence + one-click apply.</CardDescription>
            </CardHeader>
            <CardContent>
              {!selected && (
                <div className="text-center py-14 text-slate-500 text-sm" data-testid="rt-result-empty">
                  <Tag className="w-10 h-10 mx-auto mb-3 opacity-30" />
                  Pick a resource on the left to infer the owner.
                </div>
              )}
              {inferring && (
                <div className="py-14 text-center text-slate-500 text-sm">
                  <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Running inference…
                </div>
              )}
              {selected && inference && !inferring && (
                <div data-testid="rt-result">
                  <div className={`rounded-md border p-4 mb-4 ${inference.inferred_cost_center ? (inference.needs_review ? 'border-amber-500/40 bg-amber-500/10' : 'border-emerald-500/40 bg-emerald-500/10') : 'border-red-500/40 bg-red-500/10'}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Brain className="w-5 h-5 text-violet-300" />
                        <span className="text-xs uppercase tracking-widest text-slate-400">Inferred owner</span>
                        <span className="text-lg font-bold text-white" data-testid="rt-inferred-team">{inference.inferred_cost_center || 'UNKNOWN'}</span>
                      </div>
                      <Badge variant="outline" className="border-white/10 text-slate-400 font-mono">
                        confidence {(inference.confidence * 100).toFixed(0)}%
                      </Badge>
                    </div>
                    <div className="text-sm text-slate-300 mt-3 leading-relaxed">{inference.rationale}</div>
                    {inference.needs_review && (
                      <div className="text-xs text-amber-300 mt-3 flex items-center gap-1.5">
                        <AlertTriangle className="w-3.5 h-3.5" />
                        Confidence below 70% — human review recommended.
                      </div>
                    )}
                  </div>

                  {/* SIGNALS */}
                  {inference.signals_used.length > 0 && (
                    <div className="mb-4">
                      <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Signals used</div>
                      {inference.signals_used.map((s, idx) => (
                        <div key={idx} className="rounded border border-white/5 bg-slate-950 p-2.5 mb-2">
                          <div className="flex items-center justify-between text-[11px]">
                            <span className="text-violet-300 font-mono">{s.id}</span>
                            <span className="text-slate-500 font-mono">weight: {s.weight}</span>
                          </div>
                          <div className="text-xs text-slate-300 mt-1.5">{s.value}</div>
                          <div className="text-[11px] text-slate-500 mt-1">→ suggests <span className="text-white">{s.suggests}</span></div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* ALTERNATIVES */}
                  {inference.alternatives && inference.alternatives.length > 0 && (
                    <div className="mb-4">
                      <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Alternative candidates</div>
                      <div className="flex flex-wrap gap-2">
                        {inference.alternatives.map((a, idx) => (
                          <Badge key={idx} variant="outline" className="border-white/10 text-slate-400 font-mono text-[10px]">
                            {a.cost_center} ({(a.confidence * 100).toFixed(0)}%)
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* ACTION BUTTONS */}
                  {inference.inferred_cost_center && (
                    <div className="flex items-center gap-2 mt-4">
                      <Button onClick={confirmInference} className="flex-1 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold" data-testid="rt-confirm-btn">
                        <CheckCircle2 className="w-4 h-4 mr-1.5" /> Confirm + apply tag
                      </Button>
                      <Button variant="outline" className="border-white/10 text-slate-300 hover:bg-slate-950" data-testid="rt-reject-btn">
                        <X className="w-4 h-4 mr-1.5" /> Reject
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* SIGNAL CATALOG */}
        <div className="mt-16">
          <h2 className="text-base font-semibold tracking-widest text-violet-300 uppercase mb-2">Inference signals</h2>
          <p className="text-sm text-slate-400 mb-6 max-w-2xl">{signals.length} signals fed into the classifier. Each one is independently weighted; the inferencer aggregates them and emits a probability distribution over cost-centers.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="rt-signal-catalog">
            {signals.map((s) => (
              <div key={s.id} className="bg-slate-900/60 border border-white/10 rounded-md p-4">
                <div className="text-[10px] font-mono uppercase text-slate-500 mb-1">{s.id}</div>
                <div className="text-white font-semibold text-sm">{s.name}</div>
                <div className="text-xs text-slate-400 mt-1.5 leading-relaxed">{s.description}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReverseTag;
